import { createContext, FormEvent, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { Building2, Heart, Mail, MapPin, Users, X } from 'lucide-react';
import { isSupabaseConfigured, supabase } from '../../utils/supabaseClient';
import { buildWaitlistInsertRow, isWaitlistRowValid } from '../../utils/waitlist';
import '../LandingPage.css';

export type Persona = 'volunteer' | 'organisation' | 'club-charity';

type WaitlistFormState = {
  persona: Persona;
  name: string;
  email: string;
  mobileNumber: string;
  organisationName: string;
  roleTitle: string;
  location: string;
  teamSize: string;
  causeFocus: string;
};

const personaConfig: Record<
  Persona,
  {
    label: string;
    icon: typeof Users;
    summary: string;
  }
> = {
  volunteer: {
    label: 'Volunteer',
    icon: Users,
    summary: 'I want an ongoing role at a local club or community group.',
  },
  organisation: {
    label: 'Organisation',
    icon: Building2,
    summary: 'I support people looking for inclusive, ongoing volunteer roles.',
  },
  'club-charity': {
    label: 'Club / community group',
    icon: Heart,
    summary: 'I represent a club, charity, or community group.',
  },
};

const initialFormState: WaitlistFormState = {
  persona: 'volunteer',
  name: '',
  email: '',
  mobileNumber: '',
  organisationName: '',
  roleTitle: '',
  location: '',
  teamSize: '',
  causeFocus: '',
};

type WaitlistOpenOptions = {
  persona?: Persona;
};

type WaitlistContextValue = {
  openWaitlist: (options?: WaitlistOpenOptions) => void;
  prefersReducedMotion: boolean;
};

const WaitlistContext = createContext<WaitlistContextValue | null>(null);

export function useWaitlist() {
  const ctx = useContext(WaitlistContext);
  if (!ctx) throw new Error('useWaitlist must be used within MarketingLayout');
  return ctx;
}

function scrollToHash(hash: string) {
  const id = hash.replace('#', '').trim();
  if (!id) return;
  const el = document.getElementById(id);
  if (!el) return;
  el.scrollIntoView({ block: 'start', behavior: 'smooth' });
}

function waitlistAudienceLabel(persona: Persona) {
  return persona === 'volunteer' ? 'Volunteer' : 'Organisation';
}

function waitlistMailtoHref(persona: Persona) {
  const subject = `Join the VolunteerCo waitlist - ${waitlistAudienceLabel(persona)}`;
  return `mailto:silas@volunteer-co.com?subject=${encodeURIComponent(subject)}`;
}

export default function MarketingLayout() {
  const location = useLocation();
  const shellRef = useRef<HTMLDivElement | null>(null);
  const modalRef = useRef<HTMLDivElement | null>(null);
  const nameInputRef = useRef<HTMLInputElement | null>(null);
  const lastActiveElementRef = useRef<HTMLElement | null>(null);

  const [navSolid, setNavSolid] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(() => {
    return window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false;
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [emailEntry, setEmailEntry] = useState('');
  const [formState, setFormState] = useState<WaitlistFormState>(initialFormState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [useEmailFallback, setUseEmailFallback] = useState(() => !isSupabaseConfigured);

  const openWaitlist = useCallback((options?: WaitlistOpenOptions) => {
    setIsSubmitted(false);
    setSubmitError('');
    setUseEmailFallback(!isSupabaseConfigured);
    lastActiveElementRef.current = document.activeElement as HTMLElement | null;
    setFormState((current) => ({
      ...current,
      persona: options?.persona ?? current.persona,
      email: emailEntry.trim() ? emailEntry.trim() : current.email,
    }));
    setIsModalOpen(true);
  }, [emailEntry]);

  const ctxValue = useMemo<WaitlistContextValue>(() => ({ openWaitlist, prefersReducedMotion }), [
    openWaitlist,
    prefersReducedMotion,
  ]);

  const handleChange = (field: keyof WaitlistFormState, value: string) => {
    setFormState((current) => ({ ...current, [field]: value }));
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    setIsSubmitting(true);
    setSubmitError('');

    if (!isSupabaseConfigured) {
      setUseEmailFallback(true);
      setIsSubmitting(false);
      return;
    }

    const row = buildWaitlistInsertRow(formState);

    if (!isWaitlistRowValid(row)) {
      setSubmitError('Please enter your name and a valid email address.');
      setIsSubmitting(false);
      return;
    }

    // Plain insert only: anon has INSERT, not SELECT; never chain .select().
    const { error } = await supabase.from('waitlist').insert(row);

    if (error) {
      setUseEmailFallback(true);
      setIsSubmitting(false);
      return;
    }

    setEmailEntry(row.email);
    setIsSubmitted(true);
    setIsSubmitting(false);
  };

  useEffect(() => {
    const handleScroll = () => setNavSolid(window.scrollY > 8);
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const query = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (!query) return undefined;

    const handleChange = (event: MediaQueryListEvent) => setPrefersReducedMotion(event.matches);
    setPrefersReducedMotion(query.matches);

    query.addEventListener?.('change', handleChange);
    return () => query.removeEventListener?.('change', handleChange);
  }, []);

  useEffect(() => {
    const root = shellRef.current;
    if (!root) return undefined;

    if (prefersReducedMotion) {
      root.querySelectorAll<HTMLElement>('[data-reveal]').forEach((el) => {
        el.classList.add('is-revealed');
      });
      return undefined;
    }

    const elements = Array.from(root.querySelectorAll<HTMLElement>('[data-reveal]'));
    if (elements.length === 0) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          (entry.target as HTMLElement).classList.add('is-revealed');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.12 }
    );

    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [location.pathname, prefersReducedMotion]);

  useEffect(() => {
    if (prefersReducedMotion) return undefined;

    const hero = shellRef.current?.querySelector<HTMLElement>('[data-hero-parallax]');
    if (!hero) return undefined;

    let raf = 0;
    const update = () => {
      const rect = hero.getBoundingClientRect();
      const vh = window.innerHeight || 1;
      const progress = Math.min(1, Math.max(0, (vh - rect.top) / (vh + rect.height)));
      const translate = (progress - 0.5) * 34; // px
      hero.style.setProperty('--hero-parallax', `${translate.toFixed(2)}px`);
    };

    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = window.requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [location.pathname, prefersReducedMotion]);

  useEffect(() => {
    if (location.hash) {
      const t = window.setTimeout(() => scrollToHash(location.hash), 0);
      return () => window.clearTimeout(t);
    }

    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    return undefined;
  }, [location.hash, location.pathname]);

  useEffect(() => {
    if (!isModalOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    nameInputRef.current?.focus();

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsModalOpen(false);
    };

    const handleTabTrap = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;
      if (!modalRef.current) return;

      const focusable = Array.from(
        modalRef.current.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => !el.hasAttribute('disabled') && !el.getAttribute('aria-hidden'));

      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement as HTMLElement | null;

      if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      } else if (event.shiftKey && (active === first || active === modalRef.current)) {
        event.preventDefault();
        last.focus();
      }
    };

    window.addEventListener('keydown', handleEscape);
    window.addEventListener('keydown', handleTabTrap);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleEscape);
      window.removeEventListener('keydown', handleTabTrap);
    };
  }, [isModalOpen]);

  useEffect(() => {
    if (isModalOpen) return;
    lastActiveElementRef.current?.focus?.();
  }, [isModalOpen]);

  return (
    <WaitlistContext.Provider value={ctxValue}>
      <div className="landing-shell" ref={shellRef}>
        <a className="skip-link" href="#main">
          Skip to content
        </a>

        <header className="lp-nav" data-solid={navSolid ? 'true' : 'false'}>
          <div className="lp-nav-inner">
            <Link className="lp-brand" to="/" aria-label="VolunteerCo home">
              VolunteerCo<span className="lp-brand-dot">.</span>
            </Link>
            <nav className="lp-nav-links" aria-label="Primary">
              <Link to="/volunteers">For Volunteers</Link>
              <Link to="/organisations">For Organisations</Link>
              <Link to={{ pathname: '/', hash: '#impact' }}>Our Impact</Link>
            </nav>
            <div className="lp-nav-actions">
              <button className="lp-nav-signin" type="button" onClick={() => openWaitlist()}>
                Get started
              </button>
            </div>
          </div>
        </header>

        <main className="lp-main" id="main">
          <Outlet />
        </main>

        <footer className="lp-footer">
          <div className="lp-container lp-footer-inner">
            <span>VolunteerCo · Australia</span>
            <span>
              <a href="mailto:silas@volunteer-co.com">silas@volunteer-co.com</a>
              {' · '}
              <Link to="/privacy">Privacy</Link>
            </span>
          </div>
        </footer>

        {isModalOpen && (
          <div className="waitlist-modal-backdrop" role="presentation" onClick={() => setIsModalOpen(false)}>
            <div
              className="waitlist-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="waitlist-modal-title"
              onClick={(event) => event.stopPropagation()}
              ref={modalRef}
            >
              <button
                className="waitlist-close"
                type="button"
                onClick={() => setIsModalOpen(false)}
                aria-label="Close waitlist form"
              >
                <X size={18} />
              </button>

              {!isSubmitted ? (
                <>
                  <div className="waitlist-modal-header">
                    <h2 id="waitlist-modal-title">Register your interest</h2>
                    <p>For volunteers, clubs, community groups and organisations interested in the first VolunteerCo pilot.</p>
                  </div>

                  {useEmailFallback ? (
                    <div className="waitlist-email-fallback">
                      <p>Join by email and we will add you to the list.</p>
                      <a className="waitlist-submit" href={waitlistMailtoHref(formState.persona)}>
                        Join by email
                      </a>
                      <p className="waitlist-privacy">
                        We’ll only use your details to contact you about VolunteerCo. We won’t share them.{' '}
                        <Link to="/privacy">Privacy</Link>
                      </p>
                    </div>
                  ) : (
                  <form className="waitlist-form" onSubmit={handleSubmit}>
                    <fieldset className="persona-selector">
                      <legend>I am a...</legend>
                      <div className="persona-selector-grid">
                        {(Object.keys(personaConfig) as Persona[]).map((persona) => {
                          const config = personaConfig[persona];
                          const Icon = config.icon;
                          const isActive = formState.persona === persona;

                          return (
                            <button
                              key={persona}
                              type="button"
                              className={`persona-selector-button${isActive ? ' active' : ''}`}
                              onClick={() => handleChange('persona', persona)}
                              aria-pressed={isActive}
                            >
                              <span className="persona-selector-icon">
                                <Icon size={16} />
                              </span>
                              <span className="persona-selector-label">
                                <span>{config.label}</span>
                                <span className="persona-selector-summary">{config.summary}</span>
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </fieldset>

                    <label>
                      <span>{formState.persona === 'volunteer' ? 'Full name' : 'Contact name'}</span>
                      <input
                        ref={nameInputRef}
                        required
                        type="text"
                        value={formState.name}
                        onChange={(event) => handleChange('name', event.target.value)}
                        placeholder="Your name"
                        autoComplete="name"
                      />
                    </label>

                    <label>
                      <span>Email address</span>
                      <input
                        required
                        type="email"
                        value={formState.email}
                        onChange={(event) => handleChange('email', event.target.value)}
                        placeholder="you@example.com"
                        autoComplete="email"
                      />
                    </label>

                    <label>
                      <span>Mobile number</span>
                      <input
                        type="tel"
                        value={formState.mobileNumber}
                        onChange={(event) => handleChange('mobileNumber', event.target.value)}
                        placeholder="04xx xxx xxx"
                        autoComplete="tel"
                      />
                    </label>

                    {formState.persona !== 'volunteer' && (
                      <>
                        <label>
                          <span>Organisation, club, or community group name</span>
                          <input
                            type="text"
                            value={formState.organisationName}
                            onChange={(event) => handleChange('organisationName', event.target.value)}
                            placeholder="Your organisation or group"
                          />
                        </label>

                        <label>
                          <span>Your role</span>
                          <input
                            type="text"
                            value={formState.roleTitle}
                            onChange={(event) => handleChange('roleTitle', event.target.value)}
                            placeholder="Coordinator, committee member, founder…"
                          />
                        </label>

                        <label>
                          <span>Location</span>
                          <input
                            type="text"
                            value={formState.location}
                            onChange={(event) => handleChange('location', event.target.value)}
                            placeholder="City, suburb, or region"
                          />
                        </label>
                      </>
                    )}

                    {formState.persona === 'club-charity' && (
                      <>
                        <label>
                          <span>Approximate community size</span>
                          <input
                            type="text"
                            value={formState.teamSize}
                            onChange={(event) => handleChange('teamSize', event.target.value)}
                            placeholder="Members, players, families, volunteers…"
                          />
                        </label>

                        <label>
                          <span>Community focus</span>
                          <input
                            type="text"
                            value={formState.causeFocus}
                            onChange={(event) => handleChange('causeFocus', event.target.value)}
                            placeholder="Sport, fundraising, outreach, support…"
                          />
                        </label>
                      </>
                    )}

                    {submitError && <p className="waitlist-error">{submitError}</p>}

                    <button className="waitlist-submit" type="submit" disabled={isSubmitting}>
                      {isSubmitting ? 'Registering…' : 'Join the waitlist'}
                    </button>

                    <p className="waitlist-privacy">
                      We’ll only use your details to contact you about VolunteerCo. We won’t share them.{' '}
                      <Link to="/privacy">Privacy</Link>
                    </p>
                  </form>
                  )}
                </>
              ) : (
                <div className="waitlist-success">
                  <div className="waitlist-success-icon">
                    <Mail size={24} />
                  </div>
                  <h2>You’re on the list.</h2>
                  <p>Thanks. We’ve registered your interest and will be in touch as the first pilot gets going.</p>
                  <div className="waitlist-success-meta">
                    <span>
                      <Mail size={16} />
                      {formState.email}
                    </span>
                    {formState.location && (
                      <span>
                        <MapPin size={16} />
                        {formState.location}
                      </span>
                    )}
                  </div>
                  <button className="waitlist-submit" type="button" onClick={() => setIsModalOpen(false)}>
                    Close
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </WaitlistContext.Provider>
  );
}

